import { useCallback, useEffect, useState } from "react";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getSessions, revokeSession } from "../api/auth";
import { getApiError } from "../api/errors";
import PageHeader from "../components/PageHeader";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

function DetailRow({ label, value, chip }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={750}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.45 }}>
        {chip || <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{value || "—"}</Typography>}
      </Box>
    </Box>
  );
}

export default function Settings() {
  const {
    user,
    role,
    selectedWorkspace,
    selectedStore,
    platformRole,
    contextReady,
    logout,
    logoutAll,
  } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getSessions();
      setSessions(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load account sessions."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const revoke = async (session) => {
    const message = session.current
      ? "Sign out this device now?"
      : "Sign out this device? It will need to log in again.";
    if (!window.confirm(message)) return;

    setBusyId(session.public_id);
    setError("");
    try {
      await revokeSession(session.public_id);
      if (session.current) {
        await logout();
        navigate("/login", { replace: true });
        return;
      }
      setSessions((current) => current.filter((item) => item.public_id !== session.public_id));
      setSuccess("Device signed out successfully.");
    } catch (requestError) {
      setError(getApiError(requestError, "Could not sign out that device."));
    } finally {
      setBusyId("");
    }
  };

  const revokeAll = async () => {
    if (!window.confirm("Sign out every device, including this one?")) return;
    try {
      await logoutAll();
      navigate("/login", { replace: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Could not sign out all devices."));
    }
  };

  return (
    <MainLayout>
      <PageHeader
        eyebrow="Account"
        title="Settings & security"
        description="Review your profile, current store access, and the devices signed in to your account."
      />

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.3} alignItems="center">
                <Box sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center" }}>
                  <PersonRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h6">Your account</Typography>
                  <Typography variant="body2" color="text.secondary">Personal sign-in information</Typography>
                </Box>
              </Stack>
              <Stack spacing={2.1} sx={{ mt: 2.75 }}>
                <DetailRow label="Username" value={user?.username} />
                <DetailRow label="Email" value={user?.email} />
                <DetailRow
                  label="Account status"
                  chip={<Chip label={user?.is_active ? "Active" : titleCase(user?.status)} color={user?.is_active ? "success" : "error"} size="small" />}
                />
                <DetailRow label="Member since" value={formatDate(user?.created_at)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.3} alignItems="center">
                <Box sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: "#f5f3ff", color: "#7c3aed", display: "grid", placeItems: "center" }}>
                  <StorefrontRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h6">Current access</Typography>
                  <Typography variant="body2" color="text.secondary">Workspace and role selected in the top bar</Typography>
                </Box>
              </Stack>

              {contextReady ? (
                <Stack spacing={2.1} sx={{ mt: 2.75 }}>
                  <DetailRow label="Business" value={selectedWorkspace?.name} />
                  <DetailRow label="Store" value={selectedStore?.name} />
                  <DetailRow label="Your role" chip={<Chip label={titleCase(role)} size="small" variant="outlined" />} />
                  <DetailRow label="Store timezone" value={selectedStore?.timezone || "Not set"} />
                  {platformRole && (
                    <DetailRow label="Platform access" chip={<Chip label={titleCase(platformRole)} color="warning" size="small" variant="outlined" />} />
                  )}
                </Stack>
              ) : (
                <Stack spacing={2} sx={{ mt: 2.75 }}>
                  <DetailRow label="Platform role" chip={<Chip label={titleCase(platformRole || "none")} color={platformRole ? "warning" : "default"} size="small" />} />
                  <Typography color="text.secondary">
                    No store workspace is currently selected for this account.
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2}>
                <Stack direction="row" spacing={1.3} alignItems="center">
                  <Box sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: "#f0fdf4", color: "#16a34a", display: "grid", placeItems: "center" }}>
                    <DevicesRoundedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6">Signed-in devices</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
                      Sign out devices you no longer use or recognize.
                    </Typography>
                  </Box>
                </Stack>
                <Button color="error" variant="outlined" onClick={revokeAll}>
                  Sign out all devices
                </Button>
              </Stack>

              {loading ? (
                <LoadingPanel label="Loading signed-in devices…" minHeight={220} />
              ) : sessions.length === 0 ? (
                <Box sx={{ mt: 3 }}>
                  <EmptyPanel icon="🔐" title="No active sessions" description="No signed-in devices were returned for this account." />
                </Box>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 3 }}>
                  {sessions.map((session) => (
                    <Box
                      key={session.public_id}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: session.current ? "1px solid #93c5fd" : "1px solid #e2e8f0",
                        bgcolor: session.current ? "#eff6ff" : "#fff",
                      }}
                    >
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography fontWeight={900}>{session.user_agent || "Unknown device"}</Typography>
                            {session.current && <Chip label="This device" size="small" color="primary" />}
                            <Chip label={titleCase(session.status)} size="small" variant="outlined" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            Last used: {formatDate(session.last_used_at)}
                            {session.created_ip ? ` · IP ${session.created_ip}` : ""}
                          </Typography>
                        </Box>
                        <Button
                          color="error"
                          variant="outlined"
                          disabled={busyId === session.public_id}
                          onClick={() => revoke(session)}
                          sx={{ alignSelf: { xs: "stretch", md: "center" } }}
                        >
                          {busyId === session.public_id ? <CircularProgress size={20} /> : "Sign out"}
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Alert severity="info" icon={<SecurityRoundedIcon />}>
            Sessions are revocable and protected by short-lived access tokens. Sign out any device you do not recognize.
          </Alert>
        </Grid>
      </Grid>
    </MainLayout>
  );
}
